/**
 * Configuration for date mapping
 * All values are configurable to allow easy changes
 */
export const DATE_MAPPER_CONFIG = {
  // Weekend definition: 0 = Sunday, 6 = Saturday
  weekendStart: 6, // Saturday
  weekendEnd: 0, // Sunday
  // Date format: 'dd-mm-yyyy' or 'yyyy-mm-dd'
  dateFormat: "dd-mm-yyyy" as const,
  // Range separator in the message
  rangeSeparator: " - ",
  // Multiple ranges separator
  multipleRangesSeparator: ", "
} as const;

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

/**
 * Formats a date according to DATE_MAPPER_CONFIG.dateFormat
 * Currently supports dd-mm-yyyy format
 */
const formatDate = (date: Date): string => {
  // For now, we only support dd-mm-yyyy format
  // If DATE_MAPPER_CONFIG.dateFormat changes, this function should be updated accordingly
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Gets the start of today (00:00:00)
 */
const getTodayStart = (): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

/**
 * Gets the end of today (23:59:59)
 */
const getTodayEnd = (): Date => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  return today;
};

/**
 * Gets the start of tomorrow (00:00:00)
 */
const getTomorrowStart = (): Date => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  return tomorrow;
};

/**
 * Gets the end of tomorrow (23:59:59)
 */
const getTomorrowEnd = (): Date => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(23, 59, 59, 999);
  return tomorrow;
};

/**
 * Gets the current Saturday and Sunday of this week
 * If today is Saturday or Sunday, returns this weekend
 * Otherwise, returns the upcoming weekend
 */
const getThisWeekend = (): DateRange => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 6 = Saturday

  let saturday: Date;
  let sunday: Date;

  if (dayOfWeek === DATE_MAPPER_CONFIG.weekendStart) {
    // Today is Saturday
    saturday = new Date(today);
    sunday = new Date(today);
    sunday.setDate(sunday.getDate() + 1);
  } else if (dayOfWeek === DATE_MAPPER_CONFIG.weekendEnd) {
    // Today is Sunday
    saturday = new Date(today);
    saturday.setDate(saturday.getDate() - 1);
    sunday = new Date(today);
  } else {
    // Find next Saturday
    const daysUntilSaturday =
      (DATE_MAPPER_CONFIG.weekendStart - dayOfWeek + 7) % 7;
    saturday = new Date(today);
    saturday.setDate(saturday.getDate() + (daysUntilSaturday || 7));
    sunday = new Date(saturday);
    sunday.setDate(sunday.getDate() + 1);
  }

  saturday.setHours(0, 0, 0, 0);
  sunday.setHours(23, 59, 59, 999);

  return {
    startDate: saturday,
    endDate: sunday
  };
};

/**
 * Gets the next week (Monday to Sunday)
 */
const getNextWeek = (): DateRange => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 6 = Saturday
  // Calculate days until next Monday (1)
  // If today is Monday, we want next Monday
  const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek) % 7 || 7;

  const nextMonday = new Date(today);
  nextMonday.setDate(nextMonday.getDate() + daysUntilMonday);
  nextMonday.setHours(0, 0, 0, 0);

  const nextSunday = new Date(nextMonday);
  nextSunday.setDate(nextSunday.getDate() + 6);
  nextSunday.setHours(23, 59, 59, 999);

  return {
    startDate: nextMonday,
    endDate: nextSunday
  };
};

/**
 * Gets the next month (first day to last day)
 */
const getNextMonth = (): DateRange => {
  const today = new Date();
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  nextMonth.setHours(0, 0, 0, 0);

  // Last day of next month
  const lastDayOfNextMonth = new Date(
    nextMonth.getFullYear(),
    nextMonth.getMonth() + 1,
    0
  );
  lastDayOfNextMonth.setHours(23, 59, 59, 999);

  return {
    startDate: nextMonth,
    endDate: lastDayOfNextMonth
  };
};

/**
 * Maps a date key to its corresponding date range
 * @param dateKey - One of: "today", "tomorrow", "this_weekend", "next_week", "next_month"
 * @returns DateRange object or null if key is not recognized
 */
export const mapDateKeyToRange = (dateKey: string): DateRange | null => {
  switch (dateKey) {
    case "today":
      return {
        startDate: getTodayStart(),
        endDate: getTodayEnd()
      };
    case "tomorrow":
      return {
        startDate: getTomorrowStart(),
        endDate: getTomorrowEnd()
      };
    case "this_weekend":
      return getThisWeekend();
    case "next_week":
      return getNextWeek();
    case "next_month":
      return getNextMonth();
    default:
      return null;
  }
};

/**
 * Formats a date range using a language-friendly format
 * @param range - Date range to format
 * @param language - Language code (es, ca, en)
 * @returns Formatted string like "del dia 01-01-2026 al 31-01-2026" (CA)
 */
export const formatDateRange = (
  range: DateRange,
  language: string = "ca"
): string => {
  const start = formatDate(range.startDate);
  const end = formatDate(range.endDate);

  // Determine language prefix and suffix
  const lang = language.toLowerCase().substring(0, 2);
  let prefix = "";
  let connector = "";

  if (lang === "es") {
    prefix = "del día ";
    connector = " al ";
  } else if (lang === "ca") {
    prefix = "del dia ";
    connector = " al ";
  } else {
    // Default to English
    prefix = "from ";
    connector = " to ";
  }

  return `${prefix}${start}${connector}${end}`;
};

/**
 * Maps multiple date keys to formatted date ranges
 * @param dateKeys - Array of date keys to convert
 * @param language - Language code (es, ca, en)
 * @returns String with all ranges separated by DATE_MAPPER_CONFIG.multipleRangesSeparator
 */
export const mapDateKeysToRangesString = (
  dateKeys: string[],
  language: string = "ca"
): string => {
  const ranges = dateKeys
    .map((key) => mapDateKeyToRange(key))
    .filter((range): range is DateRange => range !== null)
    .map((range) => formatDateRange(range, language));

  return ranges.join(DATE_MAPPER_CONFIG.multipleRangesSeparator);
};

/**
 * Replaces date text in a message with numeric date ranges
 * This function detects date keys in selectedItems and replaces the corresponding
 * translated text in the message with the numeric ranges
 *
 * @param message - The message containing date text to replace
 * @param selectedDateKeys - Array of date keys (e.g., ["today", "next_week"])
 * @param translatedDateTexts - Array of translated date texts matching the keys
 * @param language - Language code (es, ca, en) for formatting the date ranges
 * @returns Message with date text replaced by numeric ranges
 *
 * Strategy:
 * - For single date: replace the date text directly
 * - For multiple dates: replace the entire sequence of dates (with conjunctions) with all ranges
 */
export const replaceDateTextWithRanges = (
  message: string,
  selectedDateKeys: string[],
  translatedDateTexts: string[],
  language: string = "ca"
): string => {
  if (selectedDateKeys.length === 0) {
    return message;
  }

  // Get the numeric ranges for all selected dates
  const rangesString = mapDateKeysToRangesString(selectedDateKeys, language);

  let result = message;

  if (selectedDateKeys.length === 1) {
    // Single date: simple replacement
    const escapedText = translatedDateTexts[0].replace(
      /[.*+?^${}()|[\]\\]/g,
      "\\$&"
    );
    const regex = new RegExp(escapedText, "gi");
    result = result.replace(regex, rangesString);
  } else {
    // Multiple dates: need to replace the entire sequence
    // The dates appear in the message as a list with conjunctions (e.g., "Este fin de semana y Próximo mes")
    // We'll build a pattern that matches this entire sequence

    // Escape all date texts
    const escapedTexts = translatedDateTexts.map((text) =>
      text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    );

    // Try to match the sequence in order (as they appear in translatedDateTexts)
    // Pattern: date1 (optional separator) date2 (optional separator) ...
    // Separators can be: ", ", " y ", " i ", " and ", etc.
    const separatorPattern = "\\s*[,y]\\s*";

    // Build pattern: match dates in sequence with optional separators between them
    let patternParts: string[] = [];
    for (let i = 0; i < escapedTexts.length; i++) {
      patternParts.push(escapedTexts[i]);
      if (i < escapedTexts.length - 1) {
        patternParts.push(separatorPattern);
      }
    }

    const fullPattern = new RegExp(patternParts.join(""), "gi");

    // Try to replace the full pattern
    if (fullPattern.test(result)) {
      result = result.replace(fullPattern, rangesString);
    } else {
      // Fallback: if pattern doesn't match (maybe different order or separators),
      // replace each date individually and then deduplicate
      translatedDateTexts.forEach((text) => {
        const regex = new RegExp(
          text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
          "gi"
        );
        result = result.replace(regex, rangesString);
      });

      // Deduplicate: if rangesString appears multiple times, keep only one
      const escapedRanges = rangesString.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const rangesRegex = new RegExp(escapedRanges, "g");
      const matches = result.match(rangesRegex);
      if (matches && matches.length > 1) {
        // Replace all occurrences with a single one
        result = result.replace(rangesRegex, rangesString);
      }
    }
  }

  return result;
};
